import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { sharedTranslations } from '../translations/shared';
import '../App.css';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const [locationsDropdownOpen, setLocationsDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const t = sharedTranslations[language] || sharedTranslations.en;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        setLocationsDropdownOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = `/${targetId}`;
      return;
    }
    
    const element = document.querySelector(targetId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
    setLocationsDropdownOpen(false);
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setLocationsDropdownOpen(false);
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img 
            src="https://i.postimg.cc/jSxZjXKr/logo-(2).webp" 
            alt="Champion Maker Logo" 
            className="logo-image"
          />
        </Link>
        
        <ul className="nav-menu">
          <li><Link to="/" className={location.pathname === '/' ? 'active' : ''}>{t.header.home}</Link></li>
          <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>{t.header.aboutUs}</Link></li>
          <li><Link to="/packages" className={location.pathname === '/packages' ? 'active' : ''}>{t.header.ourPackages}</Link></li>
          <li><Link to="/locations" className={location.pathname === '/locations' || location.pathname.startsWith('/locations/') ? 'active-nav' : ''}>{t.header.locations}</Link></li>
          <li><Link to="/contact" className={location.pathname === '/contact' ? 'active-nav' : ''}>{t.header.contact}</Link></li>
          <li className="language-switcher">
            <button 
              className={language === 'en' ? 'active' : ''} 
              onClick={() => setLanguage('en')}
            >
              🇬🇧 EN
            </button>
            <button 
              className={language === 'ar' ? 'active' : ''} 
              onClick={() => setLanguage('ar')}
            >
              🇸🇦 العربية
            </button>
          </li>
        </ul>
        
        <Link to="/book" className="book-now-btn">{t.header.bookNow}</Link>
        <div className="mobile-menu-wrapper" ref={dropdownRef}>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          
          {mobileMenuOpen && (
            <div className="mobile-dropdown-menu">
              <Link 
                to="/" 
                onClick={handleLinkClick} 
                className={`mobile-dropdown-item ${location.pathname === '/' ? 'active' : ''}`}
              >
                {t.header.home}
              </Link>
              <Link 
                to="/about" 
                onClick={handleLinkClick} 
                className={`mobile-dropdown-item ${location.pathname === '/about' ? 'active' : ''}`}
              >
                {t.header.aboutUs}
              </Link>
              <Link 
                to="/packages" 
                onClick={handleLinkClick} 
                className={`mobile-dropdown-item ${location.pathname === '/packages' ? 'active' : ''}`}
              >
                {t.header.ourPackages}
              </Link>
              <div className="mobile-dropdown-locations">
                <button 
                  className={`mobile-dropdown-item mobile-dropdown-toggle ${location.pathname === '/locations' || location.pathname.startsWith('/locations/') ? 'active' : ''}`}
                  onClick={() => setLocationsDropdownOpen(!locationsDropdownOpen)}
                >
                  {t.header.locations} <FaChevronDown className={`dropdown-arrow ${locationsDropdownOpen ? 'open' : ''}`} />
                </button>
                {locationsDropdownOpen && (
                  <div className="mobile-dropdown-submenu">
                    <Link 
                      to="/locations" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations' ? 'active' : ''}`}
                    >
                      {t.header.allLocations}
                    </Link>
                    <Link 
                      to="/locations/sofia-mladost" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations/sofia-mladost' ? 'active' : ''}`}
                    >
                      {t.header.sofiaMladost}
                    </Link>
                    <Link 
                      to="/locations/sofia-nsa" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations/sofia-nsa' ? 'active' : ''}`}
                    >
                      {t.header.sofiaNsa}
                    </Link>
                    <Link 
                      to="/locations/plovdiv" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations/plovdiv' ? 'active' : ''}`}
                    >
                      {t.header.plovdiv}
                    </Link>
                    <Link 
                      to="/locations/sofia-boyana" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations/sofia-boyana' ? 'active' : ''}`}
                    >
                      {t.header.sofiaBoyana}
                    </Link>
                    <Link 
                      to="/locations/kranevo" 
                      onClick={handleLinkClick} 
                      className={`mobile-dropdown-subitem ${location.pathname === '/locations/kranevo' ? 'active' : ''}`}
                    >
                      {t.header.kranevo}
                    </Link>
                  </div>
                )}
              </div>
              <Link 
                to="/contact" 
                onClick={handleLinkClick} 
                className={`mobile-dropdown-item mobile-dropdown-item-no-border ${location.pathname === '/contact' ? 'active' : ''}`}
              >
                {t.header.contact}
              </Link>
              <Link 
                to="/book" 
                onClick={handleLinkClick} 
                className="mobile-dropdown-item mobile-dropdown-book"
              >
                {t.header.bookNow}
              </Link>
              <div className="mobile-dropdown-language">
                <button 
                  className={`mobile-dropdown-lang-btn ${language === 'en' ? 'active' : ''}`} 
                  onClick={() => setLanguage('en')}
                >
                  🇬🇧 EN
                </button>
                <button 
                  className={`mobile-dropdown-lang-btn ${language === 'ar' ? 'active' : ''}`} 
                  onClick={() => setLanguage('ar')}
                >
                  🇸🇦 العربية
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Header;
